/* eslint-disable @typescript-eslint/class-methods-use-this */
// eslint-disable-next-line import/named
import { Handler } from 'mitt';

import dataStore from '../data/data-store';
import { CustomType } from '../public_api';
import { OriginValidationService } from '../utils/origin-validation-service';
import { Sanitizer } from '../utils/sanitizer';
import emitter from './emitter';
import { CustomEvents, EventData, EventType } from './events-models';

export class EventBridge {
    private source?: string;
    private iframe?: HTMLIFrameElement;
    private originValidationService: OriginValidationService;
    private sanitizer: Sanitizer;
    private controller: AbortController;
    private signal: AbortSignal;
    private allowedOrigin: string;
    private parentOrigin: string;
    constructor() {
        this.originValidationService = new OriginValidationService();
        this.sanitizer = new Sanitizer();
        this.controller = new AbortController();
        this.signal = this.controller.signal;
        this.allowedOrigin = this.getCookie('x-allowed-host') ?? 'ngpg.bwin.com';
        this.parentOrigin = this.getParentOrigin();
    }
    getParentOrigin(): string {
        try {
            if (this.originValidationService.isAllowedOrigin(new URL(document.referrer).host, this.allowedOrigin)) {
                return document.referrer;
            }
        } catch {
            return '';
        }
        return '';
    }

    initializeIframe(isProd = true) {
        if (this.source === 'iframe') {
            return;
        }
        if (!isProd) {
            this.parentOrigin = document.referrer;
        }
        this.source = 'iframe';
        this.initializeEventListener(isProd);
    }

    initializeParent() {
        if (this.source === 'parent') {
            return;
        }
        this.source = 'parent';
        const iframeElement = document.getElementById('card-iframe');
        if (iframeElement instanceof HTMLIFrameElement) {
            this.iframe = iframeElement;
            this.initializeEventListener();
        } else {
            throw new Error("Element with ID 'card-iframe' is not an iframe.");
        }
    }

    sendMessageToIframe(eventData: EventData) {
        this.iframe?.contentWindow?.postMessage(eventData, dataStore.getData('ngpgHost'));
    }

    sendMessageToParent(payLoad: EventData) {
        window.parent.postMessage(payLoad, this.parentOrigin);
    }

    private initializeEventListener(isProd = true) {
        window.addEventListener(
            'message',
            (event: MessageEvent) => {
                if (event.origin === window.location.origin) {
                    return;
                }
                if (!this.isValidOrigin(event.origin) && isProd) {
                    return;
                }

                if (!((event.data as EventData).eventName in EventType)) {
                    return;
                }

                emitter.emit((event.data as EventData).eventName as keyof CustomEvents, this.sanitizer.sanitize((event.data as EventData).payload));
            },
            { signal: this.signal },
        );
    }

    receiveEvent<K extends keyof CustomEvents>(eventName: K, callback: (data: CustomEvents[K]) => void) {
        emitter.on(eventName, callback);
    }

    off<K extends keyof CustomEvents>(eventName: K, handler?: Handler<CustomType>) {
        const handlers = emitter.all.get(eventName);
        if (handlers) {
            if (handler) {
                handlers.splice(handlers.indexOf(handler), 1);
            } else {
                emitter.all.delete(eventName);
            }
        }
    }

    removeAll() {
        this.controller.abort();
        emitter.all.clear();
    }

    private isValidOrigin(origin: string): boolean {
        if (this.source === 'iframe') {
            return this.originValidationService.isAllowedOrigin(origin, this.allowedOrigin);
        }
        return this.source === 'parent' && origin.includes(dataStore.getData('ngpgHost'));
    }
    private getCookie(name: string): string | null {
        const match = document.cookie.match(new RegExp(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`));
        return match ? decodeURIComponent(match.pop() || '') : null;
    }
}
